import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import * as React from "react";

import {
  SkillAccessibility,
  SkillSummary,
  UpdateSkillMetadataRequest,
  validateSkillName,
} from "@/shared/domain/skills";
import {
  useKnownSkillTags,
  useUpdateSkillMetadata,
} from "@/shared/domain/skills/hooks";
import { Button } from "@/shared/foundation/primitives";

import styles from "./SkillEditor.module.css";
import { TagsAutocomplete } from "./TagsAutocomplete";

interface Props {
  open: boolean;
  onClose: () => void;
  /** The skill being edited. ``null`` means the dialog is closed. */
  summary: SkillSummary | null;
  orgId: string | undefined;
}

interface FormState {
  name: string;
  accessibility: SkillAccessibility;
  tags: string[];
}

const EMPTY: FormState = {
  name: "",
  accessibility: "private",
  tags: [],
};

function seedForm(summary: SkillSummary): FormState {
  return {
    name: summary.skill.name,
    accessibility: summary.skill.accessibility,
    tags: [...summary.skill.tags],
  };
}

/**
 * Trim, drop empties, and dedupe case-insensitively. When `existing` is
 * supplied, a tag that matches an existing tag case-insensitively is
 * collapsed to the stored casing — without this, retyping a stored "QA"
 * as "qa" produces put_tags=["qa"] + remove_tags=["QA"], a silent
 * case-rename via the changeset.
 */
function normalizeTags(
  tags: string[],
  existing: readonly string[] = [],
): string[] {
  const existingByKey = new Map(existing.map((t) => [t.toLowerCase(), t]));
  const seen = new Set<string>();
  const result: string[] = [];
  for (const raw of tags) {
    const trimmed = raw.trim();
    if (trimmed.length === 0) {
      continue;
    }
    const key = trimmed.toLowerCase();
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    result.push(existingByKey.get(key) ?? trimmed);
  }
  return result;
}

function diffTags(
  before: string[],
  after: string[],
): { put_tags: string[]; remove_tags: string[] } {
  const beforeSet = new Set(before);
  const afterSet = new Set(after);
  const put_tags = after.filter((t) => !beforeSet.has(t));
  const remove_tags = before.filter((t) => !afterSet.has(t));
  return { put_tags, remove_tags };
}

/**
 * Edits skill-level metadata only — name, accessibility, tags. Version
 * content (description, body, auto_invoke) is edited separately in
 * :class:`SkillEditor`. This split is intentional: a single dialog mixing
 * "the whole skill" with "the version you're editing" was confusing
 * authors about which fields belonged to which row.
 */
export function SkillMetadataEditor({ open, onClose, summary, orgId }: Props) {
  const [form, setForm] = React.useState<FormState>(EMPTY);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

  const knownTagsQuery = useKnownSkillTags(orgId);
  const updateMetadata = useUpdateSkillMetadata(orgId);

  // Reseed only when the dialog (re)opens or the target skill changes.
  // Key on skill_id, not on the `summary` reference: the parent re-derives
  // `summary` from the skills query each render, so depending on the object
  // identity would reseed (and clobber the user's in-progress edits) every
  // time `useUpdateSkillMetadata` invalidates the listing.
  React.useEffect(() => {
    if (!open || summary === null) {
      return;
    }
    setForm(seedForm(summary));
    setErrorMessage(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, summary?.skill.skill_id]);

  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSave() {
    setErrorMessage(null);
    if (summary === null) {
      return;
    }

    const trimmedName = form.name.trim();
    // Blanking the name and clicking Save used to silently no-op the rename
    // because the changed-check required a non-empty new name. Treat empty
    // as invalid up front so the user sees "Name is required" instead of
    // the dialog closing without their edit.
    if (trimmedName.length === 0) {
      setErrorMessage(validateSkillName(""));
      return;
    }
    const nameChanged = trimmedName !== summary.skill.name;
    if (nameChanged) {
      const nameError = validateSkillName(trimmedName);
      if (nameError !== null) {
        setErrorMessage(nameError);
        return;
      }
    }

    // TagsAutocomplete commits pending text on blur — by the time this
    // handler runs (after the Save button's click), any typed-but-not-
    // Enter'd tag is already in form.tags. Just dedupe.
    const normalizedTags = normalizeTags(form.tags, summary.skill.tags);
    const tagDiff = diffTags(summary.skill.tags, normalizedTags);
    const tagsChanged =
      tagDiff.put_tags.length > 0 || tagDiff.remove_tags.length > 0;

    const accessibilityChanged =
      form.accessibility !== summary.skill.accessibility;

    if (!nameChanged && !tagsChanged && !accessibilityChanged) {
      onClose();
      return;
    }

    const request: UpdateSkillMetadataRequest = {};
    if (nameChanged) {
      request.name = trimmedName;
    }
    if (accessibilityChanged) {
      request.accessibility = form.accessibility;
    }
    if (tagDiff.put_tags.length > 0) {
      request.put_tags = tagDiff.put_tags;
    }
    if (tagDiff.remove_tags.length > 0) {
      request.remove_tags = tagDiff.remove_tags;
    }

    try {
      await updateMetadata.mutateAsync({
        skillId: summary.skill.skill_id,
        request,
      });
      onClose();
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : "Failed to save.");
    }
  }

  const title = summary === null ? "Edit skill" : `Edit ${summary.skill.name}`;

  const trimmedNameForDisplay = form.name.trim();
  // Show the "Name is required" error inline as soon as the user clears the
  // field, not only after they try to save. The pre-edit state (name still
  // matching the stored value) is the only case where no error is shown.
  const liveNameError =
    summary !== null && trimmedNameForDisplay !== summary.skill.name
      ? validateSkillName(trimmedNameForDisplay)
      : null;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <div className={styles.formStack}>
          <TextField
            label="Name"
            value={form.name}
            onChange={(e) => update("name", e.target.value)}
            error={liveNameError !== null}
            helperText={
              liveNameError ??
              "Letters, digits, hyphens, and underscores. No spaces."
            }
            required
            fullWidth
          />
          <FormControl fullWidth>
            <InputLabel id="metadata-accessibility-label">
              Accessibility
            </InputLabel>
            <Select
              labelId="metadata-accessibility-label"
              label="Accessibility"
              value={form.accessibility}
              onChange={(e) =>
                update("accessibility", e.target.value as SkillAccessibility)
              }
            >
              <MenuItem value="private">Private (only you)</MenuItem>
              <MenuItem value="org">Org (everyone in your org)</MenuItem>
            </Select>
          </FormControl>
          <TagsAutocomplete
            value={form.tags}
            onChange={(next) => update("tags", next)}
            options={knownTagsQuery.data ?? []}
            helperText="Free-form labels for grouping skills. Press Enter or click out to add."
          />
          {errorMessage && (
            <small className={styles.errorMessage}>{errorMessage}</small>
          )}
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={updateMetadata.isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            void handleSave();
          }}
          disabled={updateMetadata.isPending || liveNameError !== null}
          loading={updateMetadata.isPending}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
